/**
 * `useSelectionContextStore` — single-slot reference to whatever text
 * the lawyer most recently selected inside a `[data-selectable-source]`
 * viewer surface. The `ChatInput` reads it to render the
 * `SelectionContextChip` below its textarea and (on send) sends the
 * selection as an INVISIBLE system-event context via the runtime's
 * `runtime.system.enqueue_event` channel — so the agent knows what
 * "this" / "isso" / "este texto" refers to without the lawyer pasting
 * a quote into the visible message.
 *
 * Single-slot: a new selection REPLACES the previous one (no stacking).
 * Cleared on send, on dismiss-×, or when the user collapses the
 * selection (e.g. clicks elsewhere in the viewer without dragging).
 *
 * The store is universal — `useSelectionCapture` is mounted at the
 * App-shell level so selection works in EVERY composer instance
 * (case-chat, the global/org-scope thread, research, anywhere
 * `ChatInput` mounts). Per-viewer wiring is just the
 * `data-selectable-source={file.path}` attribute on the viewer's
 * scrollable content container.
 */

import { create } from "zustand";

/** Hard ceiling matching `runtime.system.enqueue_event`'s server-side
 *  text cap (lexruntime `05db5028`). The store truncates above this. */
export const SELECTION_MAX_CHARS = 8000;

export interface SelectionContext {
  /** The selected text, trimmed of outer whitespace, possibly truncated
   *  to `SELECTION_MAX_CHARS`. */
  text: string;
  /** Source attribution — typically the workspace-relative file path
   *  from the viewer's `data-selectable-source` attribute. */
  source: string;
  /** Number of characters in `text` AFTER trim + truncation. */
  charCount: number;
  /** Whether the original selection was longer than `SELECTION_MAX_CHARS`
   *  and got truncated. Surfaced in the chip as a "…" indicator. */
  truncated: boolean;
  /** Live DOM Range covering the original selection — used by
   *  `useSelectionHighlight` to paint a custom CSS Highlight that
   *  PERSISTS even after focus moves into the composer textarea
   *  (where the browser's native ::selection rendering disappears).
   *  Null when capture was synthetic (no DOM range available, e.g.
   *  programmatic tests). */
  range: Range | null;
}

interface SelectionContextState {
  selection: SelectionContext | null;
  /** Producer — `useSelectionCapture` calls this with the raw selection
   *  + source + optional live DOM range. Empty / collapsed selections
   *  call `clear()` instead. */
  capture: (rawText: string, source: string, range?: Range | null) => void;
  clear: () => void;
}

export const useSelectionContextStore = create<SelectionContextState>((set, get) => ({
  selection: null,
  capture: (rawText: string, source: string, range: Range | null = null) => {
    const normalized = normalizeSelection(rawText, source, range);
    // Skip the `set` when the new selection equals the current one —
    // `selectionchange` fires on every caret tick (even when the
    // visible selection is unchanged), so without this guard we'd
    // push a fresh object reference every tick and re-render every
    // chip consumer. `normalizeSelection` always returns a fresh
    // object, so referential equality won't help; compare by value.
    // (Range is intentionally excluded from equality — only text +
    // source determine identity; same selection always re-attaches
    // to the same range visually.)
    const current = get().selection;
    if (selectionsEqual(current, normalized)) return;
    set({ selection: normalized });
  },
  clear: () => {
    if (get().selection === null) return;
    set({ selection: null });
  },
}));

/** Value-equality on the two fields that determine identity. The
 *  `charCount` + `truncated` fields are derived from `text`, so
 *  comparing `text + source` is sufficient. Exported for unit test. */
export function selectionsEqual(
  a: SelectionContext | null,
  b: SelectionContext | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.text === b.text && a.source === b.source;
}

/**
 * Pure helper — normalizes a raw `window.getSelection().toString()` into
 * the store-ready `SelectionContext` shape. Exported so the unit test
 * can lock the trim + truncate + char-count rules without spinning up
 * the store.
 *
 * Returns `null` semantically (via empty-after-trim) when the raw
 * selection was nothing but whitespace — the capture call site treats
 * this as a no-op and does NOT update the store (existing selection
 * stays put; a stray click in the viewer shouldn't wipe a real
 * selection the user is mid-prompt about).
 */
export function normalizeSelection(
  rawText: string,
  source: string,
  range: Range | null = null,
): SelectionContext | null {
  const trimmed = rawText.trim();
  if (!trimmed) return null;
  const truncated = trimmed.length > SELECTION_MAX_CHARS;
  const text = truncated ? trimmed.slice(0, SELECTION_MAX_CHARS) : trimmed;
  return {
    text,
    source,
    charCount: text.length,
    truncated,
    range,
  };
}

/**
 * Build the condensed preview shown in the chip body. Head + tail with
 * an ellipsis when the selection is longer than `headLen + tailLen`,
 * otherwise the full text. Mirrors the operator brief's example shape
 * (`Selected: "este cliente disse…final."`).
 *
 * Pure helper — exported for the unit test.
 */
export function buildSelectionPreview(
  text: string,
  headLen = 36,
  tailLen = 14,
): string {
  // Collapse whitespace runs so multi-line selections preview as a
  // single line (the chip is one row tall).
  const single = text.replace(/\s+/g, " ").trim();
  if (single.length <= headLen + tailLen + 1) return single;
  const head = single.slice(0, headLen).trimEnd();
  const tail = single.slice(-tailLen).trimStart();
  return `${head}…${tail}`;
}

/**
 * Build the SPA-composed directive that gets sent invisibly to the
 * concierge via `runtime.system.enqueue_event`. The user-selected
 * text is wrapped in datamarked delimiter tags
 * (`<user_selected_context source="…">…</user_selected_context>`) so
 * the agent's prompt structure treats it as a REFERENCE/data block,
 * not an instruction — OWASP LLM01 spotlighting per the
 * `prompt-injection-safe-authoring` convention.
 *
 * `truncated` selections get an explicit marker so the agent knows
 * the reference is partial.
 *
 * Pure helper — exported for unit test.
 */
export function buildSelectionDirective(selection: SelectionContext): string {
  const truncatedNote = selection.truncated ? " (truncated)" : "";
  return (
    `The user has selected the following text from "${selection.source}"${truncatedNote}. ` +
    `When the user refers to "this", "this text", "isso", "este trecho" etc., ` +
    `they are pointing at the content between the markers below.\n\n` +
    `<user_selected_context source="${selection.source}">\n` +
    selection.text +
    `\n</user_selected_context>`
  );
}
